import axios from 'axios';
import {
  Categoria, Proveedor, Producto, MovimientoInventario, AlertaStock,
  Cliente, Venta, DetalleVenta, DireccionEntrega, Repartidor,
  PedidoOnline, PedidoOnlineListItem, SeguimientoPedido,
  ConfiguracionDelivery, EstadisticasPedidos, CrearPedidoOnline,
  EstadoPedido, ProductoFormData, MovimientoFormData, VentaFormData
} from '../types';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

// Configurar axios con base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejo de errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Error en la API:', error);
    return Promise.reject(error);
  }
);

// Servicios para Productos
export const categoriaService = {
  list: (): Promise<Categoria[]> => 
    api.get('/productos/categorias/').then(res => res.data.results || res.data),
  
  create: (data: Partial<Categoria>): Promise<Categoria> => 
    api.post('/productos/categorias/', data).then(res => res.data),
  
  update: (id: number, data: Partial<Categoria>): Promise<Categoria> => 
    api.put(`/productos/categorias/${id}/`, data).then(res => res.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/productos/categorias/${id}/`),
};

export const proveedorService = {
  list: (): Promise<Proveedor[]> => 
    api.get('/productos/proveedores/').then(res => res.data.results || res.data),
  
  create: (data: Partial<Proveedor>): Promise<Proveedor> => 
    api.post('/productos/proveedores/', data).then(res => res.data),
  
  update: (id: number, data: Partial<Proveedor>): Promise<Proveedor> => 
    api.put(`/productos/proveedores/${id}/`, data).then(res => res.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/productos/proveedores/${id}/`),
};

export const productoService = {
  list: (): Promise<Producto[]> => 
    api.get('/productos/productos/').then(res => res.data.results || res.data),
  
  get: (id: number): Promise<Producto> => 
    api.get(`/productos/productos/${id}/`).then(res => res.data),
  
  create: (data: ProductoFormData): Promise<Producto> => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    return api.post('/productos/productos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  
  update: (id: number, data: Partial<ProductoFormData>): Promise<Producto> => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    return api.put(`/productos/productos/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },
  
  delete: (id: number): Promise<void> => 
    api.delete(`/productos/productos/${id}/`),
  
  stockBajo: (): Promise<Producto[]> => 
    api.get('/productos/productos/stock_bajo/').then(res => res.data),
};

// Servicios para Inventario
export const movimientoInventarioService = {
  list: (params?: { producto?: number; tipo?: string }): Promise<MovimientoInventario[]> => 
    api.get('/inventario/movimientos/', { params }).then(res => res.data.results || res.data),
  
  create: (data: MovimientoFormData): Promise<MovimientoInventario> => 
    api.post('/inventario/movimientos/', data).then(res => res.data),
  
  estadisticas: (): Promise<any> => 
    api.get('/inventario/movimientos/estadisticas/').then(res => res.data),
};

export const alertaService = {
  list: (): Promise<AlertaStock[]> => 
    api.get('/inventario/alertas-stock/').then(res => res.data.results || res.data),
  
  resolve: (id: number): Promise<void> => 
    api.post(`/inventario/alertas-stock/${id}/resolver/`),
};

// Servicios para Ventas
export const clienteService = {
  list: (): Promise<Cliente[]> => 
    api.get('/ventas/clientes/').then(res => res.data.results || res.data),
  
  get: (id: number): Promise<Cliente> => 
    api.get(`/ventas/clientes/${id}/`).then(res => res.data),
  
  create: (data: Partial<Cliente>): Promise<Cliente> => 
    api.post('/ventas/clientes/', data).then(res => res.data),
  
  update: (id: number, data: Partial<Cliente>): Promise<Cliente> => 
    api.put(`/ventas/clientes/${id}/`, data).then(res => res.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/ventas/clientes/${id}/`),
};

export const ventaService = {
  list: (params?: { fecha_desde?: string; fecha_hasta?: string; cliente?: number }): Promise<Venta[]> => 
    api.get('/ventas/ventas/', { params }).then(res => res.data.results || res.data),
  
  get: (id: number): Promise<Venta> => 
    api.get(`/ventas/ventas/${id}/`).then(res => res.data),
  
  create: (data: VentaFormData): Promise<Venta> => 
    api.post('/ventas/ventas/', data).then(res => res.data),
  
  update: (id: number, data: Partial<VentaFormData>): Promise<Venta> => 
    api.put(`/ventas/ventas/${id}/`, data).then(res => res.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/ventas/ventas/${id}/`),
  
  estadisticas: (): Promise<any> => 
    api.get('/ventas/ventas/estadisticas/').then(res => res.data),
  
  ventasHoy: (): Promise<Venta[]> => 
    api.get('/ventas/ventas/ventas_hoy/').then(res => res.data),
};

// Servicios para Pedidos Online
export const direccionEntregaService = {
  list: (clienteId?: number): Promise<DireccionEntrega[]> => {
    const params = clienteId ? { cliente: clienteId } : {};
    return api.get('/pedidos/direcciones-entrega/', { params }).then(res => res.data.results || res.data);
  },
  
  create: (data: Partial<DireccionEntrega>): Promise<DireccionEntrega> => 
    api.post('/pedidos/direcciones-entrega/', data).then(res => res.data),
  
  update: (id: number, data: Partial<DireccionEntrega>): Promise<DireccionEntrega> => 
    api.put(`/pedidos/direcciones-entrega/${id}/`, data).then(res => res.data),
  
  delete: (id: number): Promise<void> => 
    api.delete(`/pedidos/direcciones-entrega/${id}/`),
};

export const repartidorService = {
  list: (params?: { activo?: boolean; en_servicio?: boolean; zona?: string }): Promise<Repartidor[]> => 
    api.get('/pedidos/repartidores/', { params }).then(res => res.data.results || res.data),
  
  get: (id: number): Promise<Repartidor> => 
    api.get(`/pedidos/repartidores/${id}/`).then(res => res.data),
  
  create: (data: Partial<Repartidor>): Promise<Repartidor> => 
    api.post('/pedidos/repartidores/', data).then(res => res.data),
  
  update: (id: number, data: Partial<Repartidor>): Promise<Repartidor> => 
    api.put(`/pedidos/repartidores/${id}/`, data).then(res => res.data),
  
  cambiarEstadoServicio: (id: number, enServicio: boolean): Promise<void> => 
    api.post(`/pedidos/repartidores/${id}/cambiar_estado_servicio/`, { en_servicio: enServicio }),
  
  actualizarUbicacion: (id: number, latitud: number, longitud: number): Promise<void> => 
    api.post(`/pedidos/repartidores/${id}/actualizar_ubicacion/`, { latitud, longitud }),
};

export const pedidoOnlineService = {
  list: (params?: {
    estado?: EstadoPedido;
    cliente?: number;
    repartidor?: number;
    fecha_desde?: string;
    fecha_hasta?: string;
  }): Promise<PedidoOnlineListItem[]> => 
    api.get('/pedidos/pedidos-online/', { params }).then(res => res.data.results || res.data),
  
  get: (id: number): Promise<PedidoOnline> => 
    api.get(`/pedidos/pedidos-online/${id}/`).then(res => res.data),
  
  create: (data: CrearPedidoOnline): Promise<PedidoOnline> => 
    api.post('/pedidos/pedidos-online/', data).then(res => res.data),
  
  update: (id: number, data: Partial<PedidoOnline>): Promise<PedidoOnline> => 
    api.put(`/pedidos/pedidos-online/${id}/`, data).then(res => res.data),
  
  cambiarEstado: (id: number, estado: EstadoPedido, notas?: string): Promise<PedidoOnline> => 
    api.post(`/pedidos/pedidos-online/${id}/cambiar_estado/`, { estado, notas }).then(res => res.data),
  
  asignarRepartidor: (id: number, repartidorId: number): Promise<PedidoOnline> => 
    api.post(`/pedidos/pedidos-online/${id}/asignar_repartidor/`, { repartidor: repartidorId }).then(res => res.data),
  
  calificar: (id: number, calificacion: number, comentario?: string): Promise<PedidoOnline> => 
    api.post(`/pedidos/pedidos-online/${id}/calificar/`, { calificacion, comentario_calificacion: comentario }).then(res => res.data),
  
  cancelar: (id: number, motivo?: string): Promise<PedidoOnline> => 
    api.post(`/pedidos/pedidos-online/${id}/cancelar/`, { motivo }).then(res => res.data),
  
  estadisticas: (): Promise<EstadisticasPedidos> => 
    api.get('/pedidos/pedidos-online/estadisticas/').then(res => res.data),
  
  pendientesAsignacion: (): Promise<PedidoOnlineListItem[]> => 
    api.get('/pedidos/pedidos-online/pendientes_asignacion/').then(res => res.data),
};

export const seguimientoPedidoService = {
  list: (pedidoId?: number): Promise<SeguimientoPedido[]> => {
    const params = pedidoId ? { pedido: pedidoId } : {};
    return api.get('/pedidos/seguimiento/', { params }).then(res => res.data.results || res.data);
  },
};

export const configuracionDeliveryService = {
  list: (): Promise<ConfiguracionDelivery[]> => 
    api.get('/pedidos/configuracion-delivery/').then(res => res.data.results || res.data),
  
  get: (id: number): Promise<ConfiguracionDelivery> => 
    api.get(`/pedidos/configuracion-delivery/${id}/`).then(res => res.data),
  
  getActiva: (): Promise<ConfiguracionDelivery> => 
    api.get('/pedidos/configuracion-delivery/activa/').then(res => res.data),
  
  create: (data: Partial<ConfiguracionDelivery>): Promise<ConfiguracionDelivery> => 
    api.post('/pedidos/configuracion-delivery/', data).then(res => res.data),
  
  update: (id: number, data: Partial<ConfiguracionDelivery>): Promise<ConfiguracionDelivery> => 
    api.put(`/pedidos/configuracion-delivery/${id}/`, data).then(res => res.data),
};

export default api;